import { faLock, faUser } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  Link,
  Paper,
  Typography
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../assets/id-tfja-h-white.webp";
import logoV2 from "../assets/ius-prec-Asset.svg";
import NotificationBanner from "../components/common/NotificationBanner";
import { apiService } from "../services/api/ApiService";
import { AuthResponse, CustomError, UserProfile } from "../services/api/type";

// Configuración de timeouts por operación
const TIMEOUT_CONFIG = {
  MICROSOFT_LOGIN: 25000, // 25s - Microsoft puede ser más lento por OAuth
  STANDARD_LOGIN: 15000, // 15s - Login local
  USER_PROFILE: 10000, // 10s - Perfil de usuario
  TOKEN_VALIDATION: 8000, // 8s - Validación de token
};

// Estilos
const StyledContainer = styled(Container)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
  backgroundColor: "#f7fafc",
  padding: 0,
}));

const Header = styled(Box)(({ theme }) => ({
  backgroundColor: "#8a8f95",
  padding: theme.spacing(2),
  textAlign: "center",
  color: "white",
}));

const Main = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(2),
}));

const FormWrapper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  width: "100%",
  maxWidth: "400px",
  borderRadius: theme.shape.borderRadius,
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
}));

const Footer = styled(Box)(({ theme }) => ({
  backgroundColor: "#8a8f95",
  color: "white",
  padding: theme.spacing(2),
  textAlign: "center",
}));

const LoadingOverlay = styled(Box)(() => ({
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999,
}));

const InputWrapper = styled(Box)(() => ({
  position: "relative",
  marginBottom: "16px",
}));

const IconWrapper = styled("span")(() => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: "45px",
  height: "46px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#f0f0f0",
  color: "#666",
  borderRadius: "4px 0 0 4px",
}));

const StyledInput = styled("input")(({ theme }) => ({
  width: "100%",
  height: "46px",
  padding: "12px 12px",
  paddingLeft: "55px",
  border: "1px solid #ccc",
  borderRadius: theme.shape.borderRadius,
  outline: "none",
  fontSize: "16px",
  "&:focus": {
    borderColor: "#002E74",
    boxShadow: "0 0 0 2px rgba(0, 46, 116, 0.2)",
  },
  "&:disabled": {
    backgroundColor: "#f9f9f9",
    color: "#aaa",
  },
}));

const FormFooter = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));

const LoginForm = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginStep, setLoginStep] = useState("");
  const [notification, setNotification] = useState({
    open: false,
    type: "error" as "error" | "success" | "warning" | "info",
    title: "",
    message: "",
  });

  const navigate = useNavigate();
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Cargar email guardado
  useEffect(() => {
    const savedEmail = localStorage.getItem("rememberedEmail");
    const savedRememberMe = localStorage.getItem("rememberMe") === "true";

    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(savedRememberMe);
    } else {
      // Si no hay email guardado, asegurar que rememberMe esté en false
      setRememberMe(false);
    }
  }, []);

  // Actualizar email guardado cuando el usuario cambia el email y tiene rememberMe activo
  // Solo actualiza si el email es válido para evitar guardar emails incompletos
  useEffect(() => {
    if (rememberMe && email.trim()) {
      const trimmedEmail = email.trim();
      // Solo guardar si el email es válido (completo)
      if (validateEmail(trimmedEmail)) {
        localStorage.setItem("rememberedEmail", trimmedEmail);
        localStorage.setItem("rememberMe", "true");
      }
    }
  }, [email, rememberMe]);

  // Validación de email
  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  // Manejar cambio del checkbox "Recordar usuario"
  const handleRememberMeChange = (checked: boolean) => {
    setRememberMe(checked);
    
    if (checked) {
      // Si se marca y hay un email válido, guardarlo inmediatamente
      const trimmedEmail = email.trim();
      if (trimmedEmail && validateEmail(trimmedEmail)) {
        localStorage.setItem("rememberedEmail", trimmedEmail);
        localStorage.setItem("rememberMe", "true");
      }
    } else {
      // Si se desmarca, eliminar el email guardado
      localStorage.removeItem("rememberedEmail");
      localStorage.removeItem("rememberMe");
    }
  };

  // Mostrar notificación
  const showNotification = (
    type: "error" | "success" | "warning" | "info",
    title: string,
    message: string
  ) => {
    setNotification({ open: true, type, title, message });
  };

  // Manejo de errores
  const handleError = (error: CustomError | Error) => {
    let title = "Error de Inicio de Sesión";
    let message = "Ocurrió un error inesperado. Intente nuevamente.";

    if ("response" in error && error.response) {
      const status = error.response.status;
      const errorMsg = error.response.data?.message || message;

      switch (status) {
        case 401:
          title = "Credenciales inválidas";
          message = "Verifique su correo y contraseña.";
          break;
        case 403:
          title = "Acceso denegado";
          message = "No tiene permisos para acceder a esta cuenta.";
          break;
        case 429:
          title = "Demasiados intentos";
          message = "Espere un momento antes de volver a intentar.";
          break;
        case 500:
          title = "Error del servidor";
          message =
            "El servidor no respondió correctamente. Intente más tarde.";
          break;
        default:
          message = errorMsg;
      }
    } else if (error || error.message.includes("aborted")) {
      title = "Operación cancelada";
      message = "La solicitud fue interrumpida.";
    } else if (
      error.message === "Timeout" ||
      error.message.includes("timeout")
    ) {
      title = "Tiempo de espera agotado";
      message =
        "No se pudo establecer conexión con el servidor. Verifique su red e intente nuevamente.";
    } else if ("request" in error && error.request && !error.response) {
      title = "Error de conexión";
      message =
        "No se pudo conectar al servidor. Revise su conexión a internet.";
    } else {
      console.error("[Login] Error desconocido:", error);
    }

    showNotification("error", title, message);
    setPassword("");
    setIsLoading(false);
    setLoginStep("");
  };

  // fetchWithTimeout mejorado con AbortController
  const fetchWithTimeout = useCallback(
    async <T,>(
      promise: Promise<T>,
      timeoutMs: number,
      operationName: string
    ): Promise<T> => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      const timeoutId = setTimeout(() => {
        controller.abort();
      }, timeoutMs);

      try {
        const result = await Promise.race([
          promise,
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error("Timeout")), timeoutMs)
          ),
        ]);
        return result;
      } catch (error) {
        if (controller.signal.aborted) {
          throw new Error("Request aborted");
        }
        throw error;
      } finally {
        clearTimeout(timeoutId);
      }
    },
    []
  );

  // Autenticación según dominio
  const authenticateUser = async (
    email: string,
    password: string
  ): Promise<AuthResponse> => {
    try {
      const domain = email.split("@")[1];

      if (domain === "tfja.gob.mx") {
        setLoginStep("Autenticando...");
        const response = await fetchWithTimeout(
          apiService.microsoftLogin(email, password),
          TIMEOUT_CONFIG.MICROSOFT_LOGIN,
          "microsoftLogin"
        );

        if (!response?.token) {
          throw new Error("Microsoft: credenciales inválidas");
        }

        console.log("[Login] Autenticación Microsoft exitosa");
        return response;
      } else {
        setLoginStep("Autenticando...");
        const response = await fetchWithTimeout(
          apiService.login({ email, password }),
          TIMEOUT_CONFIG.STANDARD_LOGIN,
          "standardLogin"
        );

        if (!response?.token) {
          throw new Error("Login local: credenciales inválidas");
        }

        console.log("[Login] Autenticación estándar exitosa");
        return response;
      }
    } catch (error) {
      console.error("[Login] Error en authenticateUser:", error);
      throw error;
    }
  };

  // Procesar respuesta de autenticación
  const processAuthResponse = async (
    authResponse: AuthResponse
  ): Promise<UserProfile & { role: string }> => {
    try {
      const { token, refreshToken } = authResponse;

      if (!token || !refreshToken) {
        throw new Error("Token o refreshToken faltante");
      }

      // Guardar tokens
      sessionStorage.setItem("token", token);
      sessionStorage.setItem("refreshToken", refreshToken);

      // Cargar perfil y validar token
      const [userProfile, validationResponse] = await Promise.all([
        fetchWithTimeout(
          apiService.getUserProfile(),
          TIMEOUT_CONFIG.USER_PROFILE,
          "getUserProfile"
        ),
        fetchWithTimeout(
          apiService.validateToken(token),
          TIMEOUT_CONFIG.TOKEN_VALIDATION,
          "validateToken"
        ),
      ]);

      if (!userProfile || !validationResponse) {
        throw new Error("Datos incompletos");
      }

      if (userProfile.status === "DELETED") {
        const error = new Error(
          "Usuario eliminado. Contacte al administrador."
        );
        // @ts-expect-error: Attach custom response for error handling
        error.response = {
          data: { message: "Usuario eliminado. Contacte al administrador." },
        };
        throw error;
      }

      // Asignar rol
      let userRole = "guest";
      if (validationResponse.roles.includes("Manager")) userRole = "admin";
      else if (validationResponse.roles.includes("Supervisor"))
        userRole = "Supervisor";
      else if (validationResponse.roles.includes("User")) userRole = "user";

      const userData = { ...userProfile, role: userRole };

      // Guardar datos
      localStorage.setItem("userProfile", JSON.stringify(userData));
      localStorage.setItem(
        "userPermissions",
        JSON.stringify(validationResponse.accelerators)
      );

      return userData;
    } catch (error) {
      // Limpiar en caso de error
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("refreshToken");
      localStorage.removeItem("userProfile");
      localStorage.removeItem("userPermissions");
      throw error;
    }
  };

  // Redirección según rol
  const redirectUser = (userProfile: UserProfile & { role: string }) => {
    const { role, status } = userProfile;

    if (role === "admin") {
      navigate("/dashboard");
    } else if (role === "user" || role === "Supervisor") {
      navigate(status === "ACTIVE" ? "/dashboard" : "/session-inactive");
    } else {
      navigate("/");
    }
  };

  // Manejo de submit
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();

    if (!trimmedEmail || !trimmedPassword) {
      showNotification(
        "error",
        "Campos requeridos",
        "Por favor complete todos los campos."
      );
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      showNotification(
        "error",
        "Correo inválido",
        "Ingrese un correo electrónico válido."
      );
      return;
    }

    setIsLoading(true);
    setLoginStep("Iniciando sesión...");

    try {
      // Actualizar preferencia de recordar después de validar el email
      if (rememberMe) {
        localStorage.setItem("rememberedEmail", trimmedEmail);
        localStorage.setItem("rememberMe", "true");
      } else {
        // Solo eliminar si el usuario explícitamente desmarcó el checkbox
        // (no eliminar si simplemente no lo marcó)
        const savedRememberMe = localStorage.getItem("rememberMe") === "true";
        if (!savedRememberMe) {
          localStorage.removeItem("rememberedEmail");
          localStorage.removeItem("rememberMe");
        }
      }

      const authResponse = await authenticateUser(
        trimmedEmail,
        trimmedPassword
      );
      const userProfile = await processAuthResponse(authResponse);
      
      // Después de login exitoso, asegurar que el email esté guardado si rememberMe está activo
      if (rememberMe) {
        localStorage.setItem("rememberedEmail", trimmedEmail);
        localStorage.setItem("rememberMe", "true");
      }
      
      redirectUser(userProfile);
    } catch (error) {
      handleError(error as CustomError | Error);
    } finally {
      setIsLoading(false);
      setLoginStep("");
    }
  };

  return (
    <StyledContainer maxWidth={false} disableGutters>
      {/* Header */}
      <Header>
        <img
          src={logo}
          alt="Government Seal"
          style={{ width: "400px", height: "auto" }}
        />
      </Header>

      {/* Logo Iustitia */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={{ py: 4 }}
      >
        <img
          src={logoV2}
          alt="Logo Iustitia"
          style={{ width: "200px", height: "auto" }}
        />
      </Box>

      {/* Formulario */}
      <Main>
        <FormWrapper elevation={3}>
          <Typography variant="h5" align="center" gutterBottom>
            Iniciar Sesión
          </Typography>
          <form onSubmit={handleSubmit} noValidate>
            <InputWrapper>
              <IconWrapper>
                <FontAwesomeIcon icon={faUser} size="sm" />
              </IconWrapper>
              <StyledInput
                id="email-input"
                type="email"
                placeholder="Correo electrónico"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
                ref={emailRef}
                disabled={isLoading}
              />
            </InputWrapper>

            <InputWrapper>
              <IconWrapper>
                <FontAwesomeIcon icon={faLock} size="sm" />
              </IconWrapper>
              <StyledInput
                id="password-input"
                type="password"
                placeholder="Contraseña"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                ref={passwordRef}
                disabled={isLoading}
              />
            </InputWrapper>

            <FormFooter>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => handleRememberMeChange(e.target.checked)}
                    color="primary"
                    size="small"
                    disabled={isLoading}
                  />
                }
                label={
                  <Typography variant="body2">Recordar usuario</Typography>
                }
              />
               <Link
                component="button"
                type="button"
                onClick={() => navigate("/forgot-password")}
                sx={{ color: "#002E74", fontWeight: 600, textDecoration: "none" }}
              >
                Olvidé mi contraseña
              </Link>
            </FormFooter>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                py: 1.5,
                fontWeight: "bold",
                backgroundColor: "#002E74",
                "&:hover": { backgroundColor: "#001F4D" },
                opacity: isLoading ? 0.8 : 1,
              }}
              disabled={isLoading}
            >
              {isLoading ? "PROCESANDO..." : "INGRESAR"}
            </Button>
          </form>
        </FormWrapper>
      </Main>

      {/* Footer */}
      <Footer>
        <Typography variant="body2">TFJA 2025 | Versión: 1.0</Typography>
        <Typography variant="body2">Soporte de la aplicación: Mesa de ayuda - DRAU | Ext: 67 7800 y 67 7801 | Correo: atencion.usuarios@tfja.gob.mx</Typography>
      </Footer>

      {/* Overlay de carga */}
      {isLoading && (
        <LoadingOverlay>
          <Box textAlign="center">
            <CircularProgress size={60} color="secondary" />
            {loginStep && (
              <Typography variant="body1" color="white" mt={2}>
                {loginStep}
              </Typography>
            )}
          </Box>
        </LoadingOverlay>
      )}

      {/* Notificaciones */}
      <NotificationBanner
        type={notification.type}
        title={notification.title}
        message={notification.message}
        open={notification.open}
        onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
      />
    </StyledContainer>
  );
};

export default LoginForm;
